/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaBoxOpen, FaSearch } from 'react-icons/fa';

import Loading from '../../../components/Loading';
import ProductNameSearch from '../../../components/ProductNameSearch';
import { useAllPdvs } from '../../../hooks/useAllPdvs';
import { getStockPosition } from '../../../services/inventory/transfersApi';
import { notifyApiError } from '../../../utils/feedback';

import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { DataTable } from '../../../styles/ui/Table';
import { EmptyState } from '../../../styles/feedback/EmptyState';
import { CtaButton, GhostButton } from '../../../styles/ui/Button';

// ---------------------------------------------------------------------------
// Posição de estoque com reserva e trânsito
// (GET /transfer-orders/stock-position).
//
// É a resposta ao "quanto eu tenho, onde está e em que situação". Os três
// números que não existem na consulta de saldo comum são DERIVADOS pelo
// backend, sem coluna nova:
//   · reservado  = Σ (pedido − expedido) das ordens abertas/parciais na origem;
//   · trânsito   = Σ expedido das remessas ainda não conferidas (entrando/saindo);
//   · disponível = físico − reservado.
//
// Só aparecem linhas com físico, reserva ou trânsito diferente de zero, e só
// dos PDVs do usuário — o recorte é do backend, não da tela.
//
// Refatoração visual 2026-09-10 (UI_SPEC): cores semânticas por coluna —
// reservado em âmbar, entrando +N em verde, saindo −N em azul-escuro,
// disponível negativo em vermelho; zero vira "—" (numa tabela de saldos, o
// número que importa é o que não é zero). São cores de DADO, não de ação.
//
// Sem paginação: o contrato devolve a lista completa do escopo.
// ---------------------------------------------------------------------------

const NumberCell = styled.td`
   && {
      text-align: right;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      font-weight: 700;
      color: ${({ $tone }) => {
         if ($tone === 'reserved') return '#8a5a00';
         if ($tone === 'incoming') return '#1b5e20';
         if ($tone === 'outgoing') return '#0b3a63';
         if ($tone === 'negative') return '#c62828';
         return 'inherit';
      }};
   }
`;

const Zero = styled.span`
   color: rgba(0, 0, 0, 0.35);
   font-weight: 400;
`;

const ProductCell = styled.td`
   .name {
      display: block;
      font-weight: 700;
      overflow-wrap: anywhere;
   }
   .sku {
      display: block;
      margin-top: 1px;
      font-size: 11px;
      color: ${({ theme }) => theme.colors.textMuted};
   }
`;

// Zero é ruído numa tabela de saldos — vira "—". `signed` prefixa o sinal
// (+ entrando / − saindo, sempre positivos no payload).
function QtyCell({ value = 0, tone = null, signed = null }) {
   const numeric = Number(value ?? 0);
   if (numeric === 0) return <NumberCell><Zero>—</Zero></NumberCell>;

   const isNegative = numeric < 0;
   const resolvedTone = isNegative && tone === 'default' ? 'negative' : tone;
   let text = `${numeric} un`;
   if (signed === 'plus') text = `+${numeric} un`;
   if (signed === 'minus') text = `−${numeric} un`;

   return (
      <NumberCell $tone={isNegative ? 'negative' : resolvedTone}>
         {text}
      </NumberCell>
   );
}

QtyCell.propTypes = {
   value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
   tone: PropTypes.string,
   signed: PropTypes.string,
};

export default function StockPosition() {
   const { pdvs } = useAllPdvs();

   const [searchParams, setSearchParams] = useSearchParams();
   const hasInitialQueryParams = searchParams.toString() !== '';

   const [pdvId, setPdvId] = useState(() => searchParams.get('pdv_id') ?? '');
   const [prodId, setProdId] = useState(() => searchParams.get('prod_id') ?? '');
   const [productQuery, setProductQuery] = useState('');

   const [positions, setPositions] = useState([]);
   const [isLoading, setIsLoading] = useState(false);
   const [hasSearched, setHasSearched] = useState(false);

   const didInitialFetch = useRef(false);

   const commitToUrl = useCallback(
      (nextPdvId, nextProdId) => {
         const params = { applied: '1' };
         if (nextPdvId) params.pdv_id = nextPdvId;
         if (nextProdId) params.prod_id = nextProdId;
         setSearchParams(params, { replace: true });
      },
      [setSearchParams]
   );

   const fetchPositions = useCallback(async (nextPdvId, nextProdId) => {
      setIsLoading(true);
      try {
         const result = await getStockPosition({
            pdvId: nextPdvId,
            prodId: nextProdId,
         });
         setPositions(result);
         setHasSearched(true);
      } catch (err) {
         notifyApiError(err, {
            fallback: 'Erro ao consultar a posição de estoque.',
         });
      } finally {
         setIsLoading(false);
      }
   }, []);

   // URL limpa = abertura fria, sem busca (navegacao.md §1.4).
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchPositions(pdvId, prodId);
   }, [fetchPositions, hasInitialQueryParams, pdvId, prodId]);

   const handleSearch = (event) => {
      event.preventDefault();
      commitToUrl(pdvId, prodId);
      fetchPositions(pdvId, prodId);
   };

   const handleClear = () => {
      setPdvId('');
      setProdId('');
      setProductQuery('');
      setPositions([]);
      setHasSearched(false);
      setSearchParams({}, { replace: true });
   };

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Posição de estoque</PageTitle>
               <PageSubtitle>
                  Disponível = físico − reservado. Linhas zeradas não aparecem.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Consulta</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-4">
                        <label htmlFor="position-pdv">PDV</label>
                        <select
                           id="position-pdv"
                           value={pdvId}
                           onChange={(event) => setPdvId(event.target.value)}
                        >
                           <option value="">Todos os meus PDVs</option>
                           {pdvs.map((pdv) => (
                              <option key={pdv.id} value={pdv.id}>
                                 {pdv.pdv_name ?? pdv.name ?? `PDV ${pdv.id}`}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field className="col-8">
                        <label htmlFor="position-product">Produto</label>
                        <ProductNameSearch
                           value={productQuery}
                           onChange={(value) => {
                              setProductQuery(value);
                              // Texto sem seleção não é filtro: o backend
                              // espera `prod_id`, não nome.
                              if (!value) setProdId('');
                           }}
                           onSelect={(product) =>
                              setProdId(product ? String(product.id) : '')
                           }
                           nameLike
                           displayMode="sku-name"
                           placeholder="Opcional — digite o SKU ou o nome e selecione"
                        />
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <GhostButton
                           type="button"
                           onClick={handleClear}
                           disabled={isLoading}
                        >
                           Limpar
                        </GhostButton>
                        <CtaButton type="submit" disabled={isLoading}>
                           <FaSearch size={12} aria-hidden="true" /> Consultar
                        </CtaButton>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionTitle>
                  Posição{positions.length ? ` (${positions.length})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {positions.length > 0 ? (
                  <TableWrapper $minWidth="880px">
                     <DataTable>
                        <thead>
                           <tr>
                              <th>Produto</th>
                              <th>PDV</th>
                              <th className="num">Físico</th>
                              <th className="num">Reservado</th>
                              <th className="num">Disponível</th>
                              <th className="num">Entrando</th>
                              <th className="num">Saindo</th>
                           </tr>
                        </thead>
                        <tbody>
                           {positions.map((row) => (
                              <tr key={`${row.pdv_id}-${row.prod_id}`}>
                                 <ProductCell>
                                    <span className="name">
                                       {row.prod_name ?? row.prod_id}
                                    </span>
                                    <span className="sku">
                                       SKU {row.prod_sku ?? '—'}
                                    </span>
                                 </ProductCell>
                                 <td>{row.pdv_name ?? `PDV ${row.pdv_id}`}</td>
                                 <QtyCell
                                    value={row.physical_qtd}
                                    tone="default"
                                 />
                                 <QtyCell
                                    value={row.reserved_qtd}
                                    tone="reserved"
                                 />
                                 <QtyCell
                                    value={row.available_qtd}
                                    tone="default"
                                 />
                                 <QtyCell
                                    value={row.transit_incoming_qtd}
                                    tone="incoming"
                                    signed="plus"
                                 />
                                 <QtyCell
                                    value={row.transit_outgoing_qtd}
                                    tone="outgoing"
                                    signed="minus"
                                 />
                              </tr>
                           ))}
                        </tbody>
                     </DataTable>
                  </TableWrapper>
               ) : (
                  <EmptyState
                     icon={<FaBoxOpen size={20} />}
                     title={
                        hasSearched
                           ? 'Nenhuma posição para este recorte'
                           : 'Consulte a posição de estoque'
                     }
                     description={
                        hasSearched
                           ? 'Só aparecem produtos com saldo físico, reserva ou trânsito diferente de zero.'
                           : 'Escolha o PDV e/ou o produto e clique em Consultar.'
                     }
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
